import * as React from "react"
import { Loader2 } from "lucide-react"
import { Button, type ButtonProps } from "@/components/ui/button"
import { cn } from "@/lib/utils"

// ─── MUI-style color × variant class map ───────────────────────────────────
const colorMap: Record<string, Record<string, string>> = {
  contained: {
    primary:   "bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm",
    secondary: "bg-foreground/10 text-primary hover:bg-secondary/80 shadow-sm",
    error:     "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-sm",
    success:   "bg-success text-success-foreground hover:bg-success/90 shadow-sm",
    warning:   "bg-warning text-warning-foreground hover:bg-warning/90 shadow-sm",
    info:      "bg-info text-info-foreground hover:bg-info/90 shadow-sm",
  },
  outlined: {
    primary:   "border border-primary text-primary bg-transparent hover:bg-primary/10",
    secondary: "border border-secondary text-primary bg-transparent hover:bg-secondary/20",
    error:     "border border-destructive text-destructive bg-transparent hover:bg-destructive/10",
    success:   "border border-success text-success bg-transparent hover:bg-success/10",
    warning:   "border border-warning text-warning bg-transparent hover:bg-warning/10",
    info:      "border border-info text-info bg-transparent hover:bg-info/10",
  },
  text: {
    primary:   "font-semibold text-primary bg-transparent hover:bg-primary/10 shadow-none",
    secondary: "font-semibold text-primary bg-transparent hover:bg-secondary/20 shadow-none",
    error:     "font-semibold text-destructive bg-transparent hover:bg-destructive/10 shadow-none",
    success:   "font-semibold text-success bg-transparent hover:bg-success/10 shadow-none",
    warning:   "font-semibold text-warning bg-transparent hover:bg-warning/10 shadow-none",
    info:      "font-semibold text-info bg-transparent hover:bg-info/10 shadow-none",
  },
}

// ─── Size map → shadcn size ─────────────────────────────────────────────────
const sizeMap: Record<string, ButtonProps["size"]> = {
  sm:   "sm",
  md:   "default",
  lg:   "lg",
  xl:   "xl",
  icon: "icon",
}

// ─── Size → border-radius override ─────────────────────────────────────────
const sizeRadiusMap: Record<string, string> = {
  sm:   "",
  md:   "rounded-[0.5rem]",
  lg:   "rounded-[0.5rem]",
  xl:   "rounded-[0.5rem]",
  icon: "",
}

// ─── Props ──────────────────────────────────────────────────────────────────
export interface AppButtonProps extends Omit<ButtonProps, "variant" | "size"> {
  /** MUI-style visual variant */
  variant?:   "contained" | "outlined" | "text"
  /** Semantic color token */
  color?:     "primary" | "secondary" | "error" | "success" | "warning" | "info"
  /** Button size */
  size?:      "sm" | "md" | "lg" | "xl" | "icon"
  /** Stretch to full container width */
  fullWidth?: boolean
  /** Shows spinner and disables the button */
  loading?:   boolean
  /** Alias for loading */
  isLoading?: boolean
  /** Icon rendered before the label */
  startIcon?: React.ReactNode
  /** Icon rendered after the label */
  endIcon?:   React.ReactNode
}

// ─── Component ──────────────────────────────────────────────────────────────
const AppButton = React.forwardRef<HTMLButtonElement, AppButtonProps>(
  (
    {
      className,
      variant  = "contained",
      color    = "primary",
      size     = "md",
      fullWidth = false,
      loading  = false,
      isLoading = false,
      startIcon,
      endIcon,
      disabled,
      children,
      asChild,
      ...props
    },
    ref
  ) => {
    const isBtnLoading = loading || isLoading
    const colorClass = colorMap[variant]?.[color] ?? ""

    return (
      <Button
        ref={ref}
        asChild={asChild}
        // pass "default" so shadcn doesn't add its own color classes
        variant="default"
        size={sizeMap[size]}
        disabled={disabled || isBtnLoading}
        className={cn(
          "cursor-pointer",
          colorClass,
          sizeRadiusMap[size],
          fullWidth && "w-full",
          className
        )}
        {...props}
      >
        {/* When asChild is true, Slot requires a single child — skip decorators */}
        {asChild ? children : (
          <>
            {isBtnLoading ? (
              <Loader2 className="h-4 w-4 animate-spin shrink-0" />
            ) : (
              startIcon && <span className="shrink-0 inline-flex">{startIcon}</span>
            )}
            {children}
            {!isBtnLoading && endIcon && (
              <span className="shrink-0 inline-flex">{endIcon}</span>
            )}
          </>
        )}
      </Button>
    )
  }
)
AppButton.displayName = "AppButton"

export { AppButton }
